import { Sidebar, SidebarContent, SidebarHeader } from "src/components/ui/sidebar";
import { ReactComponent as NoNameLogo } from 'src/assets/images/brand-logo.svg';
import { ReactComponent as Plus } from 'src/assets/icons-redesign/plus.svg';
import { ReactComponent as Wand } from 'src/assets/icons-redesign/wand.svg';
import { ReactComponent as AiStar } from 'src/assets/icons-redesign/ai-star.svg';
import { ReactComponent as Download } from 'src/assets/icons-redesign/download.svg';
import { ReactComponent as Wallet } from 'src/assets/icons-redesign/wallet.svg';
import { Button } from "../../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { partial } from "lodash";
import useCreateNewSurvey from "../../../hooks/useCreateNewSurvey";
import { useSelector } from "react-redux";

interface DashboardSidebarButtonProps {
  readonly onClick: () => void;
  readonly title: string;
  readonly subtitle: string;
  readonly icon: React.ReactNode;
  readonly color?: string;
  readonly isActive?: boolean;
}

const DashboardSidebarButton = ({ onClick, subtitle, title, icon, color = 'bg-blue-500', isActive = false }: DashboardSidebarButtonProps) => {
  return (
    <div
      className={`flex gap-3 p-3 rounded-3xl cursor-pointer ${isActive ? 'bg-gray-300' : 'hover:bg-gray-200'}`}
      onClick={onClick}
    >
      <div className={`flex shrink-0 size-8 ${color} rounded-full items-center justify-center self-center`}>
        {icon}
      </div>
      <div>
        <p className="font-bold">
          {title}
        </p>
        <p className="text-sm">
          {subtitle}
        </p>
      </div>
    </div>
  );
};

interface DashboardSidebarProps {
  readonly activePage?: string;
}

const DashboardSidebar = ({ activePage }: DashboardSidebarProps) => {
  const navigate = useNavigate();
  const { createNewSurvey } = useCreateNewSurvey();

  const isOrgAdmin = useSelector((state: any) => state.userState.permissions?.groups?.includes('org_admin'));

  const navTo = (path: string) => {
    navigate(path);
  };

  return (
    <Sidebar
      variant='floating'
      size="survey-results"
    >
      <SidebarHeader className="p-6">
        <Button
          className="size-8 m-1 p-0"
          onClick={partial(navTo, '/dashboard')}
        >
          <NoNameLogo className="h-[18px] fill-white" />
        </Button>
      </SidebarHeader>
      <SidebarContent className="flex flex-col p-6 gap-3">
        {isOrgAdmin && (
          <>
            <DashboardSidebarButton
              title='Auto generate a survey'
              subtitle='AI creates questions for Web, Ad/RTR, or AI audiences'
              icon={<Wand className="size-[18px] stroke-white" />}
              onClick={partial(navTo, '/generate-survey')}
            />
            <DashboardSidebarButton
              title='Manually build a survey'
              subtitle='You build questions for Web, Ad/RTR, or AI audiences'
              icon={<Plus className="size-[18px] fill-white" />}
              onClick={() => createNewSurvey()}
            />
            <DashboardSidebarButton
              title='Agent templates'
              subtitle='Explore our AI workflows for specific research goals'
              icon={<Wand className="size-[18px] stroke-white" />}
              onClick={partial(navTo, '/templates')}
            />
            {/* <DashboardSidebarButton
              title='CSV survey builder'
              subtitle='Build a survey by uploading a CSV file'
              icon={<Plus className="size-[18px] fill-white" />}
              onClick={() => createNewSurvey(true)}
            /> */}
            <hr />
          </>
        )}
        <DashboardSidebarButton
          color="bg-green-1000"
          title='Campaign reporter'
          subtitle='Generate insights from ad campaign data'
          icon={<Download className="size-[16px] mt-[-2px] ml-[-1px] fill-white" />}
          onClick={partial(navTo, '/reports')}
        />
        {isOrgAdmin && (
          <DashboardSidebarButton
            color="bg-green-1000"
            title='Billing'
            subtitle='Manage insertion orders and invoices'
            icon={<Wallet className="size-[18px] fill-white" />}
            onClick={partial(navTo, '/billing')}
            isActive={activePage === '/billing'}
          />
        )}
        {isOrgAdmin && (
          <>
            <hr />
            <DashboardSidebarButton
              color="bg-purple-500"
              title='Beta tools'
              subtitle='Preview our future tools'
              icon={<AiStar className="size-[18px] fill-white" />}
              onClick={() => window.location.href = 'https://tools.invisibly.com/'}
            />
          </>
        )}
      </SidebarContent>
    </Sidebar>
  );
};

export default DashboardSidebar;
